/**
 * Subscription activation (LUM-52).
 *
 * Ported from the three inline scripts on the live WordPress page
 * lumenate.co/subscription-confirmation/. Nothing here is invented: the redemption code is not
 * injected server-side on live either — Stripe's post-payment success redirect appends it as a
 * query parameter (`?rdc=xxxx-xxxx`) and the page builds everything from that in the browser.
 *
 * This element is the boundary for that behaviour. Wrap any markup in <lumenate-activation> and it
 * fills, inside itself only:
 *
 *   [data-activation-qr]    -> a QR of the deep link (via assets/lumenate-qrcode.js)
 *   [data-activation-link]  -> href of the "Magic Login" button
 *   [data-activation-code]  -> textContent set to the code itself
 *   [data-activation-error] -> shown, with its authored copy, when the code is missing or malformed
 *
 * Fail closed, exactly as live does. Without a valid code the QR and the link stay absent and the
 * authored `xxxx-xxxx` placeholder stays on screen, so a customer never scans a QR that resolves
 * to a broken activation. The two validations differ on purpose and are live's:
 *
 *   - the deep link accepts 8–64 chars of [A-Za-z0-9_-], a deliberately loose allowlist so a
 *     future code format still links,
 *   - the visible code is rendered only for the exact `xxxx-xxxx` shape, because that string is
 *     something a customer retypes into the app by hand — showing them anything else would be
 *     worse than showing the placeholder.
 *
 * Progressive enhancement: the section renders the copy, the placeholder and a real <a> without
 * JS. Only the QR needs scripting, and it has a visible error state when it cannot be built.
 */
(() => {
  /** Loose allowlist for anything we put in the deep link. Live's bounds. */
  const CODE_ALLOWED = /^[A-Za-z0-9_-]+$/;
  /** Exact shape of a code we are willing to show a customer: 4 alphanumerics, hyphen, 4 more. */
  const CODE_DISPLAY = /^[A-Za-z0-9]{4}-[A-Za-z0-9]{4}$/;
  const CODE_MIN = 8;
  const CODE_MAX = 64;
  /** Token the section's link template carries in place of the code. */
  const CODE_TOKEN = '{code}';

  class LumenateActivation extends HTMLElement {
    connectedCallback() {
      if (this.dataset.activationReady) return;
      this.dataset.activationReady = 'true';

      this.#applyReferrerPolicy();

      const code = this.#readCode();

      // The visible code first: it is the one part that needs no library, so a QR failure below
      // must not cost the customer their fallback route into the app.
      if (code && CODE_DISPLAY.test(code)) {
        this.querySelectorAll('[data-activation-code]').forEach((element) => {
          // textContent, never innerHTML — the value came off the URL.
          element.textContent = code;
        });
      }

      // A section may carry only the code slot — the "Other ways to connect" copy does. With no
      // link template there is nothing further to resolve, and nothing has gone wrong.
      const template = this.dataset.activationTemplate;
      if (!template) return;

      if (!code) {
        this.#fail();
        return;
      }

      const url = this.#buildUrl(code, template);
      if (!url) {
        this.#fail();
        return;
      }

      this.querySelectorAll('[data-activation-link]').forEach((link) => {
        link.href = url;
        // The URL carries the code, so keep it out of any Referer the destination would see.
        link.rel = 'noopener noreferrer';
        link.removeAttribute('aria-disabled');
      });

      this.#renderQr(url);
    }

    /**
     * Tighten the document's referrer policy from the head, where it is actually honoured.
     *
     * Live authors `<meta name="referrer" content="no-referrer">` inline in the page body, which
     * Chrome ignores — the tag is only read in the head. Doing it here covers every request the
     * page makes from this point on. Requests the theme's own head scripts already fired are not
     * covered, which is why the generated activation links set rel="noreferrer" as well: that link
     * is the only one whose destination would otherwise receive the code.
     */
    #applyReferrerPolicy() {
      const policy = this.dataset.activationReferrerPolicy;
      if (!policy) return;
      if (document.head.querySelector(`meta[name="referrer"][content="${policy}"]`)) return;

      const meta = document.createElement('meta');
      meta.name = 'referrer';
      meta.content = policy;
      document.head.prepend(meta);
    }

    /**
     * The code, or null. Read from the query parameter named by data-activation-param so a change
     * on Stripe's side is a theme-editor edit rather than a deploy.
     */
    #readCode() {
      const param = this.dataset.activationParam || 'rdc';
      const value = new URLSearchParams(window.location.search).get(param);

      if (!value) return null;
      // Placeholders that arrive as strings when something upstream interpolated a null.
      if (value === 'null' || value === 'undefined') return null;
      if (value.length < CODE_MIN || value.length > CODE_MAX) return null;
      if (!CODE_ALLOWED.test(value)) return null;

      return value;
    }

    /** The app deep link, from the section's template. Null if the template has no token. */
    #buildUrl(code, template) {
      if (!template.includes(CODE_TOKEN)) return null;

      return template.replace(CODE_TOKEN, encodeURIComponent(code));
    }

    #renderQr(url) {
      const target = this.querySelector('[data-activation-qr]');
      if (!target) return;

      if (typeof window.QRCode !== 'function') {
        this.#fail(target.dataset.qrUnavailable);
        return;
      }

      target.textContent = '';

      // Live's exact options: white modules on a transparent ground so the QR sits on the glass
      // panel rather than in a white card, at medium error correction.
      new window.QRCode(target, {
        text: url,
        width: Number(target.dataset.qrSize) || 350,
        height: Number(target.dataset.qrSize) || 350,
        colorDark: target.dataset.qrColor || '#ffffff',
        colorLight: 'rgba(0,0,0,0)',
        correctLevel: window.QRCode.CorrectLevel.M,
      });

      // qrcodejs paints a <canvas> and then swaps in an <img> of it. Neither carries a label, and
      // its `title` is the raw deep link, which is noise to a screen reader — describe the element
      // once, on the container, and hide the generated children.
      target.querySelectorAll('canvas, img, table').forEach((node) => {
        node.setAttribute('aria-hidden', 'true');
        node.removeAttribute('title');
      });
      target.removeAttribute('title');
    }

    /** Reveal the authored error copy. Optionally replace it (library-missing reads differently). */
    #fail(message) {
      this.querySelectorAll('[data-activation-error]').forEach((error) => {
        if (message) error.textContent = message;
        error.hidden = false;
      });
    }
  }

  if (!customElements.get('lumenate-activation')) {
    customElements.define('lumenate-activation', LumenateActivation);
  }
})();
